import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, Download, FolderInput, Gauge, Globe, KeyRound, Radio, Search, Upload } from 'lucide-react';
import { api } from '../api/client';
import { useMe } from '../api/hooks';
import { When, bytes, clock, dateTime, elapsed, relative } from '../lib/format';
import { StatusDot } from '../components/Status';
import { ProgressLine, QueuedPill } from '../components/JobProgress';
import { TableScroll } from '../components/TableScroll';
import { Operations } from './Operations';

interface Person { id: string; name: string; kind: 'person' | 'api_key' }
interface IngestRow {
  id: string; source: 'upload' | 'api' | 'watch' | 'other'; sourceName: string; watchFolderId: string | null; filename: string;
  sizeBytes: number | null; receivedBytes: number | null; status: string; progress: { step: string; percent: number | null; waiting?: boolean; waitingSince?: string | null } | null;
  error: string | null; notes: string[]; asset: { id: string | null; title: string; deleted: boolean; purged: boolean } | null; user: Person | null;
  workflows: Array<{ runId: string; workflowId: string; name: string; status: string }>; createdAt: string; updatedAt: string;
}
interface IngestSource { key: string; kind: 'upload' | 'api' | 'watch' | 'other'; name: string; total: number; active: number; failed: number; bytes: number; lastAt: string | null }
interface ExportRow {
  id: string; kind: 'render' | 'edl' | 'copy' | 'transcode' | 'handoff'; status: string; detail: string;
  subject: { type: 'sequence' | 'asset'; id: string; name: string; deleted?: boolean } | null;
  workflow: { id: string; name: string; runId: string } | null; user: Person | null;
  step: string | null; percent: number | null; sizeBytes: number | null; error: string | null;
  /** Queued (0.95.0): its next step waits for a free worker. */
  waiting?: boolean; waitingSince?: string | null;
  where: ExportWhere | null;
  createdAt: string; startedAt: string | null; finishedAt: string | null;
}
interface ExportWhere {
  kind: 'tier' | 'location' | 'agent' | 'asset' | 'download'; label: string; locationId: string | null;
  agent: string | null; folder: string | null; delivery: 'pending' | 'delivered' | 'failed' | null; error: string | null;
}
const DELIVERY_TEXT = { pending: 'Pending', delivered: 'Delivered', failed: 'Failed' } as const;
const DELIVERY_CLASS = { pending: 'processing', delivered: 'ready', failed: 'failed' } as const;

/** The Where column: the exports tier, a location, or agent and folder, with the delivery state. */
function WhereCell({ where }: { where: ExportWhere | null }) {
  if (!where) return <span className="faint">—</span>;
  return (
    <div data-testid="export-where" data-kind={where.kind}>
      <div>{where.label}</div>
      {where.kind === 'agent' && <div className="faint mono" style={{ fontSize: 12 }}>{where.agent ? `${where.agent} · ` : ''}{where.folder}</div>}
      {where.delivery && <StatusDot tone={DELIVERY_CLASS[where.delivery]} data-delivery={where.delivery} style={{ fontSize: 12 }}>{DELIVERY_TEXT[where.delivery]}</StatusDot>}
      {where.error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{where.error}</div>}
    </div>
  );
}
interface LiveRow {
  id: string; state: 'waiting' | 'recording' | 'finishing' | 'finished' | 'failed';
  source: { kind: 'source' | 'watch'; id: string | null; name: string; detail: string | null };
  asset: { id: string; title: string; deleted: boolean; status: string | null; thumbnailUrl: string | null } | null; user: Person | null;
  input: { format: string | null; bitrateKbps: number | null } | null;
  seconds: number | null; error: string | null; startedAt: string | null; finishedAt: string | null;
}
interface LiveNowCard {
  id: string; kind: 'source' | 'watch'; name: string; detail: string | null;
  state: 'idle' | 'waiting' | 'connected' | 'recording' | 'finishing'; since: string | null; seconds: number | null;
  input: { format: string | null; bitrateKbps: number | null } | null; asset: { id: string; title: string } | null;
}
interface LiveData {
  total: number; recording: number; waiting: number; finished: number; failed: number; recordedSeconds: number;
  sources: Array<{ id: string; name: string; kind: 'rtmp' | 'srt' | 'watch' }>; now: LiveNowCard[]; items: LiveRow[];
}
interface Filters { users: Person[]; workflows: Array<{ id: string; name: string }>; watchFolders: Array<{ id: string; name: string; enabled: boolean }> }

const RANGES = [
  { id: '1h', label: 'Last hour', ms: 3600_000 },
  { id: '24h', label: 'Last 24 hours', ms: 86_400_000 },
  { id: '7d', label: 'Last 7 days', ms: 7 * 86_400_000 },
  { id: '30d', label: 'Last 30 days', ms: 30 * 86_400_000 },
  { id: 'all', label: 'Any time', ms: 0 },
  { id: 'custom', label: 'Between…', ms: 0 },
];
const ACTIVE = ['uploading', 'importing', 'queued', 'processing', 'running'];
const STATUS_TEXT: Record<string, string> = {
  uploading: 'Uploading', importing: 'Importing', queued: 'Queued', processing: 'Processing', ready: 'Ready', partial: 'Partly ready',
  failed: 'Failed', duplicate: 'Duplicate', cancelled: 'Cancelled', deleted: 'Deleted', running: 'Running', succeeded: 'Done',
};
const KIND_TEXT: Record<ExportRow['kind'], string> = { render: 'Render', edl: 'EDL', copy: 'Copy', transcode: 'Transcode', handoff: 'Send to edit' };
const statusClass = (s: string) => (ACTIVE.includes(s) ? 'processing' : s === 'failed' ? 'failed' : s === 'partial' ? 'partial' : s === 'cancelled' || s === 'duplicate' || s === 'deleted' ? 'offline' : 'ready');

function StatusPill({ status }: { status: string }) {
  return <StatusDot tone={statusClass(status)} data-status={status}>{STATUS_TEXT[status] ?? status}</StatusDot>;
}

/** datetime-local value (local time) ↔ ISO. */
const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/** Ingest and export monitoring for everyone: what came in and what went out, and who or what did it. */
export function Monitoring() {
  const me = useMe().data!;
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'exports' ? 'exports' : params.get('tab') === 'live' ? 'live' : params.get('tab') === 'operations' ? 'operations' : 'ingest';
  const range = params.get('range') ?? '24h';
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v === null || v === '' ? next.delete(k) : next.set(k, v));
    setParams(next, { replace: true });
  };

  const filters = useQuery({ queryKey: ['monitoring-filters'], queryFn: () => api<Filters>('/api/v1/monitoring/filters'), staleTime: 60_000 });
  // The window is recomputed on every refetch so "last hour" keeps moving.
  const timeWindow = () => {
    if (range === 'custom') return { from: params.get('from') ?? '', to: params.get('to') ?? '' };
    const r = RANGES.find((x) => x.id === range);
    return r?.ms ? { from: new Date(Date.now() - r.ms).toISOString(), to: '' } : { from: '', to: '' };
  };
  const common = { userId: params.get('user') ?? '', workflowId: params.get('workflow') ?? '', status: params.get('status') ?? 'all', q: params.get('q') ?? '' };
  const key = [tab, range, params.get('from'), params.get('to'), common, params.get('source'), params.get('kind')];
  const query = (extra: Record<string, string>) => {
    const w = timeWindow();
    const q = new URLSearchParams(Object.entries({ ...common, ...w, ...extra, limit: '200' }).filter(([, v]) => v && v !== 'all'));
    return q.toString();
  };
  const ingest = useQuery({
    queryKey: ['monitoring-ingests', ...key], enabled: tab === 'ingest', refetchInterval: 5000,
    queryFn: () => api<{ total: number; items: IngestRow[]; sources: IngestSource[]; watchQueues?: WatchQueue[] }>(`/api/v1/monitoring/ingests?${query({ source: params.get('source') ?? '' })}`),
  });
  const exp = useQuery({
    queryKey: ['monitoring-exports', ...key], enabled: tab === 'exports', refetchInterval: 5000,
    queryFn: () => api<{ total: number; active: number; failed: number; byWorkflow: number; items: ExportRow[] }>(`/api/v1/monitoring/exports?${query({ kind: params.get('kind') ?? '' })}`),
  });
  const live = useQuery({
    queryKey: ['monitoring-live', ...key], enabled: tab === 'live', refetchInterval: 3000,
    queryFn: () => api<LiveData>(`/api/v1/monitoring/live?${query({ source: params.get('source') ?? '' })}`),
  });
  const canSeeRuns = me.permissions.includes('automation.manage') || me.permissions.includes('automation.run');
  const f = filters.data;

  return (
    <>
      <header className="topbar">
        <h1><Activity size={16} color="var(--accent-strong)" /> Monitor</h1>
        <div className="segmented" role="tablist" aria-label="What to monitor">
          <button role="tab" aria-pressed={tab === 'ingest'} onClick={() => set({ tab: null, source: null, kind: null })} data-testid="tab-ingest"><Upload size={13} /> Ingest</button>
          <button role="tab" aria-pressed={tab === 'exports'} onClick={() => set({ tab: 'exports', source: null })} data-testid="tab-exports"><Download size={13} /> Exports</button>
          <button role="tab" aria-pressed={tab === 'live'} onClick={() => set({ tab: 'live', source: null, kind: null, user: null, workflow: null })} data-testid="tab-live"><Radio size={13} /> Live</button>
          <button role="tab" aria-pressed={tab === 'operations'} onClick={() => set({ tab: 'operations', source: null, kind: null, user: null, workflow: null, q: null })} data-testid="tab-operations"><Gauge size={13} /> Operations</button>
        </div>
        {tab !== 'operations' && <div className="search" role="search">
          <Search size={15} />
          <input className="input" placeholder={tab === 'ingest' ? 'File name or title…' : tab === 'live' ? 'Title or source…' : 'Sequence or asset…'} value={common.q} onChange={(e) => set({ q: e.target.value })} aria-label="Search" />
        </div>}
      </header>
      {tab === 'operations' ? <Operations /> : <div className="page" data-testid={`monitor-${tab}`}>
        <div className="monitor-filters" data-testid="monitor-filters">
          <select className="select" value={range} aria-label="Time" onChange={(e) => set({ range: e.target.value === '24h' ? null : e.target.value, ...(e.target.value === 'custom' ? { from: timeWindow().from || new Date(Date.now() - 86_400_000).toISOString() } : { from: null, to: null }) })}>
            {/* Ingest always has a window (1.0.0-rc.13): all-time totals are in Administration → Statistics. */}
            {RANGES.filter((r) => tab !== 'ingest' || r.id !== 'all').map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          {range === 'custom' && (
            <>
              <input className="input" type="datetime-local" aria-label="From" value={toLocalInput(params.get('from'))} onChange={(e) => set({ from: e.target.value ? new Date(e.target.value).toISOString() : null })} />
              <span className="faint">to</span>
              <input className="input" type="datetime-local" aria-label="To" value={toLocalInput(params.get('to'))} onChange={(e) => set({ to: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </>
          )}
          {tab === 'ingest' ? (
            <select className="select" value={params.get('source') ?? 'all'} aria-label="Source" onChange={(e) => set({ source: e.target.value === 'all' ? null : e.target.value })}>
              <option value="all">All sources</option>
              <option value="upload">Web upload</option>
              <option value="api">Integration API</option>
              {f?.watchFolders.map((w) => <option key={w.id} value={w.id}>Watch folder: {w.name}</option>)}
              <option value="other">Registered and catalogued</option>
            </select>
          ) : tab === 'live' ? (
            <select className="select" value={params.get('source') ?? 'all'} aria-label="Source" onChange={(e) => set({ source: e.target.value === 'all' ? null : e.target.value })}>
              <option value="all">All live sources and folders</option>
              {live.data?.sources.map((x) => <option key={x.id} value={x.id}>{x.kind === 'watch' ? `Watch folder: ${x.name}` : `${x.kind.toUpperCase()}: ${x.name}`}</option>)}
            </select>
          ) : (
            <select className="select" value={params.get('kind') ?? 'all'} aria-label="Kind" onChange={(e) => set({ kind: e.target.value === 'all' ? null : e.target.value })}>
              <option value="all">All kinds</option>
              <option value="render">Rendered sequences</option>
              <option value="edl">EDLs</option>
              <option value="copy">Copies to storage</option>
              <option value="transcode">Transcodes</option>
              <option value="handoff">Sent to edit</option>
            </select>
          )}
          {tab !== 'live' && <select className="select" value={common.workflowId || 'all'} aria-label="Workflow" onChange={(e) => set({ workflow: e.target.value === 'all' ? null : e.target.value })} data-testid="filter-workflow">
            <option value="all">Any workflow</option>
            <option value="none">No workflow</option>
            {f?.workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>}
          {tab !== 'live' && <select className="select" value={common.userId || 'all'} aria-label="Person" onChange={(e) => set({ user: e.target.value === 'all' ? null : e.target.value })} data-testid="filter-user">
            <option value="all">Anyone</option>
            {f?.users.map((u) => <option key={u.id} value={u.id}>{u.kind === 'api_key' ? `API key: ${u.name}` : u.name}</option>)}
          </select>}
          <div className="segmented" role="radiogroup" aria-label="Status">
            {['all', 'active', 'failed', 'done'].map((s) => <button key={s} aria-pressed={common.status === s} onClick={() => set({ status: s === 'all' ? null : s })}>{s === 'all' ? 'All' : s[0]!.toUpperCase() + s.slice(1)}</button>)}
          </div>
        </div>
        {tab === 'ingest'
          ? <IngestView data={ingest.data} loading={ingest.isLoading} selectedSource={params.get('source')} onSource={(s) => set({ source: s })} canSeeRuns={canSeeRuns} />
          : tab === 'live' ? <LiveView data={live.data} loading={live.isLoading} />
          : <ExportView data={exp.data} loading={exp.isLoading} canSeeRuns={canSeeRuns} />}
      </div>}
    </>
  );
}

const SOURCE_ICON = { upload: <Upload size={14} />, api: <KeyRound size={14} />, watch: <FolderInput size={14} />, other: <Globe size={14} /> };

/** A watch folder with files importing or waiting for an import slot (1.0.0-rc.13). */
interface WatchQueue { watchFolderId: string; name: string; limit: number; inFlight: number; waiting: number; oldestWaitingAt: string | null }

function IngestView({ data, loading, selectedSource, onSource, canSeeRuns }: {
  data: { total: number; items: IngestRow[]; sources: IngestSource[]; watchQueues?: WatchQueue[] } | undefined; loading: boolean; selectedSource: string | null; onSource: (s: string | null) => void; canSeeRuns: boolean;
}) {
  if (loading || !data) return <div className="skeleton" style={{ height: 200 }} />;
  return (
    <>
      {(data.watchQueues ?? []).length > 0 && (
        <div className="monitor-watch-queues" data-testid="watch-queues">
          {data.watchQueues!.map((w) => (
            <span key={w.watchFolderId} className="chip" data-testid="watch-queue" data-folder={w.watchFolderId}>
              <FolderInput size={13} /> {w.name}: importing {w.inFlight.toLocaleString('en-US')}{w.waiting > 0 ? ` · waiting ${w.waiting.toLocaleString('en-US')}` : ''}
              <span className="faint"> (at most {w.limit} at a time)</span>
            </span>
          ))}
        </div>
      )}
      {data.sources.length > 0 && (
        <div className="monitor-sources" data-testid="ingest-sources">
          {data.sources.map((s) => (
            <button key={s.key} className={`monitor-source ${selectedSource === s.key ? 'active' : ''}`} onClick={() => onSource(selectedSource === s.key ? null : s.key)} data-testid="ingest-source">
              <span className="name">{SOURCE_ICON[s.kind]} {s.name}</span>
              <span className="figures">
                <strong>{s.total}</strong> file{s.total === 1 ? '' : 's'} came in · {bytes(s.bytes)}
                {s.active > 0 && <StatusDot tone="processing">{s.active} in progress</StatusDot>}
                {s.failed > 0 && <StatusDot tone="failed">{s.failed} failed</StatusDot>}
              </span>
              <span className="faint" style={{ fontSize: 12 }}>Last {relative(s.lastAt)}</span>
            </button>
          ))}
        </div>
      )}
      {data.items.length === 0 ? <p className="muted" style={{ marginTop: 24 }}>Nothing came in that matches these filters.</p> : (
        <TableScroll>
        <table className="table compact" data-testid="ingest-table" aria-label="Ingests">
          <thead><tr><th>When</th><th>Source</th><th>File</th><th>Asset</th><th>By</th><th>Status</th><th>Workflows</th><th>Size</th></tr></thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} data-testid="ingest-row">
                <When as="td" className="muted nowrap" iso={r.createdAt} />
                <td className="wrap">{SOURCE_ICON[r.source]} {r.sourceName}</td>
                <td className="mono wrap" title={r.filename} style={{ minWidth: 140 }}>{r.filename}</td>
                <td className="wrap" style={{ minWidth: 120 }}>{r.asset ? (r.asset.id ? <Link to={`/assets/${r.asset.id}`}>{r.asset.title}</Link> : <span>{r.asset.title}</span>) : <span className="faint">—</span>}{r.asset?.deleted && <span className="faint">{r.asset.purged ? ' (deleted permanently)' : ' (deleted)'}</span>}</td>
                <td className="wrap">{r.user ? r.user.name : <span className="faint">{r.source === 'watch' ? 'Watch folder' : '—'}</span>}</td>
                <td className="wrap" style={{ minWidth: 130 }}>
                  {r.progress?.waiting ? <QueuedPill since={r.progress.waitingSince} /> : <StatusPill status={r.status} />}
                  {r.status === 'uploading' && r.sizeBytes ? <ProgressLine step="uploading" percent={((r.receivedBytes ?? 0) / r.sizeBytes) * 100} /> : null}
                  {r.progress && !r.progress.waiting && <ProgressLine step={r.progress.step} percent={r.progress.percent} />}
                  {r.error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{r.error}</div>}
                </td>
                <td className="wrap">
                  {r.workflows.length === 0 ? <span className="faint">—</span> : r.workflows.map((w) => (
                    <div key={w.runId} style={{ fontSize: 12.5 }}>{canSeeRuns ? <Link to={`/workflows/runs/${w.runId}`}>{w.name}</Link> : w.name} <span className="faint">· {STATUS_TEXT[w.status] ?? w.status.replace('_', ' ')}</span></div>
                  ))}
                </td>
                <td className="mono muted nowrap">{bytes(r.sizeBytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      )}
      <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>Showing {data.items.length} of {data.total}. Updates every few seconds.</p>
    </>
  );
}

function ExportView({ data, loading, canSeeRuns }: { data: { total: number; active: number; failed: number; byWorkflow: number; items: ExportRow[] } | undefined; loading: boolean; canSeeRuns: boolean }) {
  const summary = useMemo(() => data && [
    { label: 'Exports', value: data.total },
    { label: 'In progress', value: data.active },
    { label: 'Failed', value: data.failed },
    { label: 'By a workflow', value: data.byWorkflow },
  ], [data]);
  if (loading || !data) return <div className="skeleton" style={{ height: 200 }} />;
  return (
    <>
      <div className="monitor-sources">
        {summary!.map((s) => <div key={s.label} className="monitor-source static"><span className="faint" style={{ fontSize: 12 }}>{s.label}</span><strong style={{ fontSize: 20 }}>{s.value}</strong></div>)}
      </div>
      {data.items.length === 0 ? <p className="muted" style={{ marginTop: 24 }}>No exports match these filters.</p> : (
        <TableScroll>
        <table className="table compact" data-testid="export-table" aria-label="Exports">
          <thead><tr><th>When</th><th>What</th><th>Of</th><th>Where</th><th>Workflow</th><th>By</th><th>Status</th><th>Size</th><th>Took</th></tr></thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} data-testid="export-row">
                <When as="td" className="muted nowrap" iso={r.createdAt} />
                <td className="wrap" style={{ minWidth: 140 }} title={r.detail}><strong style={{ fontWeight: 560 }}>{KIND_TEXT[r.kind]}</strong> <span className="muted">{r.detail}</span></td>
                <td className="wrap" style={{ minWidth: 110 }}>{r.subject ? <Link to={r.subject.type === 'sequence' ? `/create/${r.subject.id}` : `/assets/${r.subject.id}`}>{r.subject.name}</Link> : <span className="faint">—</span>}</td>
                <td className="wrap" style={{ minWidth: 120 }}><WhereCell where={r.where} /></td>
                <td className="wrap">{r.workflow ? (canSeeRuns ? <Link to={`/workflows/runs/${r.workflow.runId}`}>{r.workflow.name}</Link> : r.workflow.name) : <span className="faint">None</span>}</td>
                <td className="wrap">{r.user ? (r.user.kind === 'api_key' ? `API key: ${r.user.name}` : r.user.name) : <span className="faint">{r.workflow ? 'Automatic' : '—'}</span>}</td>
                <td className="wrap" style={{ minWidth: 130 }}>
                  {r.waiting ? <QueuedPill since={r.waitingSince} /> : <StatusPill status={r.status} />}
                  {r.status === 'running' && !r.waiting && <ProgressLine step={r.step} percent={r.percent} />}
                  {r.error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{r.error}</div>}
                </td>
                <td className="mono muted nowrap">{bytes(r.sizeBytes)}</td>
                <td className="mono muted nowrap">{r.finishedAt && r.startedAt ? elapsed(r.startedAt, r.finishedAt) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      )}
      <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>Showing {data.items.length} of {data.total}. Updates every few seconds.</p>
    </>
  );
}

const LIVE_TEXT: Record<LiveRow['state'], string> = { waiting: 'Waiting for a stream', recording: 'Recording', finishing: 'Ingesting', finished: 'Recorded', failed: 'Failed' };
const LIVE_CLASS: Record<LiveRow['state'], string> = { waiting: 'offline', recording: 'failed', finishing: 'processing', finished: 'ready', failed: 'failed' };
const duration = (s: number | null) => (s == null ? '' : clock(s));

const NOW_TEXT: Record<LiveNowCard['state'], string> = { idle: 'Idle', waiting: 'Waiting', connected: 'Connected', recording: 'Recording', finishing: 'Ingesting' };
const NOW_CLASS: Record<LiveNowCard['state'], string> = { idle: 'offline', waiting: 'processing', connected: 'processing', recording: 'failed', finishing: 'processing' };
const kbps = (k: number | null) => (k == null ? null : k >= 1000 ? `${(k / 1000).toFixed(1)} Mb/s` : `${k} kb/s`);

/** One card per live source and live watch folder: what it is doing now. */
function LiveNow({ cards }: { cards: LiveNowCard[] }) {
  if (!cards.length) return null;
  return (
    <section className="live-now" aria-label="Live now" data-testid="live-now">
      <h3>Live now</h3>
      <div className="live-now-cards">
        {cards.map((c) => (
          <div key={c.id} className={`live-now-card ${c.state}`} data-testid="live-now-card" data-state={c.state} data-name={c.name}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {c.kind === 'watch' ? <FolderInput size={14} /> : <Radio size={14} />}
              <strong className="truncate" style={{ flex: 1 }}>{c.name}</strong>
              <StatusDot tone={NOW_CLASS[c.state]} data-status={c.state}>{NOW_TEXT[c.state]}</StatusDot>
            </div>
            {c.detail && <div className="faint mono truncate" style={{ fontSize: 12 }}>{c.detail}</div>}
            <div className="live-now-facts">
              {c.seconds != null && <span className="mono" title={c.since ? dateTime(c.since) : ''}>{duration(c.seconds)}</span>}
              {c.input?.format && <span>{c.input.format}</span>}
              {c.input?.bitrateKbps != null && <span className="mono">{kbps(c.input.bitrateKbps)}</span>}
            </div>
            {c.asset && <Link to={`/assets/${c.asset.id}`} className="truncate" data-testid="live-now-asset">{c.asset.title}</Link>}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Live recordings: streams to live sources and growing files in live watch folders. */
function LiveView({ data, loading }: { data: LiveData | undefined; loading: boolean }) {
  if (loading || !data) return <div className="skeleton" style={{ height: 200 }} />;
  const cards = [
    { label: 'Recording now', value: String(data.recording) },
    { label: 'Waiting for a stream', value: String(data.waiting) },
    { label: 'Recorded', value: String(data.finished) },
    { label: 'Failed', value: String(data.failed) },
    { label: 'Recorded time', value: duration(data.recordedSeconds) },
  ];
  return (
    <>
      <LiveNow cards={data.now} />
      <div className="monitor-sources" data-testid="live-summary">
        {cards.map((c) => <div key={c.label} className="monitor-source static"><span className="faint" style={{ fontSize: 12 }}>{c.label}</span><strong style={{ fontSize: 20 }}>{c.value}</strong></div>)}
      </div>
      {data.items.length === 0 ? <p className="muted" style={{ marginTop: 24 }}>No live recordings match these filters.</p> : (
        <TableScroll>
        <table className="table compact" data-testid="live-table" aria-label="Live recordings">
          <thead><tr><th>Started</th><th>Source</th><th>Recording</th><th>By</th><th>Status</th><th>Length</th></tr></thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} data-testid="live-row" data-state={r.state}>
                <td className="muted nowrap" title={r.startedAt ? dateTime(r.startedAt) : ''}>{relative(r.startedAt)}</td>
                <td className="wrap" style={{ minWidth: 140 }}>{r.source.kind === 'watch' ? <FolderInput size={14} /> : <Radio size={14} />} {r.source.name}{r.source.detail && <div className="faint mono" style={{ fontSize: 12 }}>{r.source.detail}</div>}</td>
                <td style={{ minWidth: 180 }}>
                  {r.asset ? (
                    <div className="live-asset" style={{ maxWidth: 320 }}>
                      {r.asset.thumbnailUrl ? <img src={r.asset.thumbnailUrl} alt="" className="live-thumb" data-testid="live-thumb" /> : <span className="live-thumb empty" />}
                      <div style={{ minWidth: 0 }}>
                        <div className="truncate">{r.asset.title}{r.asset.deleted && <span className="faint"> (deleted)</span>}</div>
                        {!r.asset.deleted && <Link to={`/assets/${r.asset.id}`} style={{ fontSize: 12 }} data-testid="live-open-asset">Open asset</Link>}
                        {r.input?.format && <div className="faint" style={{ fontSize: 12 }}>{r.input.format}</div>}
                      </div>
                    </div>
                  ) : <span className="faint">—</span>}
                </td>
                <td className="wrap">{r.user ? r.user.name : <span className="faint">{r.source.kind === 'watch' ? 'Watch folder' : '—'}</span>}</td>
                <td className="wrap" style={{ minWidth: 130 }}>
                  <StatusDot tone={LIVE_CLASS[r.state]} data-status={r.state}>{LIVE_TEXT[r.state]}</StatusDot>
                  {r.state === 'finished' && r.asset?.status && r.asset.status !== 'ready' && <span className="faint" style={{ fontSize: 12 }}> · asset {STATUS_TEXT[r.asset.status]?.toLowerCase() ?? r.asset.status}</span>}
                  {r.error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{r.error}</div>}
                </td>
                <td className="mono muted nowrap">{r.state === 'waiting' ? '' : duration(r.seconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      )}
      <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>Showing {data.items.length} of {data.total}. Updates every few seconds.</p>
    </>
  );
}
